import type {Editor} from "@tiptap/core";
import type {Mark, Node as ProseMirrorNode} from "@tiptap/pm/model";
import {NodeSelection} from "@tiptap/pm/state";
import type {} from "@tiptap/extension-invisible-characters";
import {DEFAULT_PRINTOUT_PARAGRAPH_VARIANT, type PrintoutParagraphVariant} from "../../blocks";
import {
    BLOCK_FORMATS,
    getCurrentBlockFormat,
    getCurrentTextAlign,
    isHeadingBlockFormat,
    isInList,
    isParagraphBlockFormat,
    HEADING_LEVEL_BY_BLOCK_FORMAT,
    type BlockFormat,
    type HeadingLevel,
    type TextAlignment,
} from "./printoutToolbarFormats";

export interface ToolbarState {
    canAlignCenter: boolean;
    canAlignJustify: boolean;
    canAlignLeft: boolean;
    canAlignRight: boolean;
    canBlockquote: boolean;
    canBold: boolean;
    canBulletList: boolean;
    canCode: boolean;
    canItalic: boolean;
    canOrderedList: boolean;
    canBlockFormat: Record<BlockFormat, boolean>;
    canRedo: boolean;
    canStrike: boolean;
    canSubscript: boolean;
    canSuperscript: boolean;
    canUndo: boolean;
    canUnderline: boolean;
    invisibleCharactersVisible: boolean;
    isBold: boolean;
    isBulletList: boolean;
    isCode: boolean;
    isItalic: boolean;
    isOrderedList: boolean;
    isStrike: boolean;
    isSubscript: boolean;
    isSuperscript: boolean;
    isUnderline: boolean;
    blockFormat: BlockFormat;
    highlightColor: string | null;
    textAlign: TextAlignment;
    textColor: string | null;
    isBlockSelected: boolean;
    isBlockquote: boolean;
}

export const DEFAULT_TOOLBAR_STATE: ToolbarState = {
    canAlignCenter: false,
    canAlignJustify: false,
    canAlignLeft: false,
    canAlignRight: false,
    canBlockquote: false,
    canBold: false,
    canBlockFormat: Object.fromEntries(BLOCK_FORMATS.map(format => [format, false])) as Record<BlockFormat, boolean>,
    canBulletList: false,
    canCode: false,
    canItalic: false,
    canOrderedList: false,
    canRedo: false,
    canStrike: false,
    canSubscript: false,
    canSuperscript: false,
    canUndo: false,
    canUnderline: false,
    invisibleCharactersVisible: false,
    isBold: false,
    isBulletList: false,
    isCode: false,
    isItalic: false,
    isOrderedList: false,
    isStrike: false,
    isSubscript: false,
    isSuperscript: false,
    isUnderline: false,
    blockFormat: DEFAULT_PRINTOUT_PARAGRAPH_VARIANT,
    highlightColor: null,
    textAlign: 'left',
    textColor: null,
    isBlockSelected: false,
    isBlockquote: false,
};

const PRINTOUT_VARIABLE_NODE_NAME = "printoutVariable";

// Only marks that remain meaningful after variable resolution can be added to chips.
export const PRINTOUT_VARIABLE_FORMAT_MARKS = ["bold", "italic", "underline", "strike"] as const;

export type PrintoutVariableFormatMark = typeof PRINTOUT_VARIABLE_FORMAT_MARKS[number];

export type PrintoutVariableMarkName = PrintoutVariableFormatMark | "highlight" | "textStyle";

const PRINTOUT_VARIABLE_EXCLUDED_MARKS = ["code", "subscript", "superscript"] as const;

export interface PrintoutVariableTarget {
    node: ProseMirrorNode;
    pos: number;
}

export const getSelectedPrintoutVariable = (editor: Editor): PrintoutVariableTarget | null => {
    const {selection} = editor.state;

    return selection instanceof NodeSelection && selection.node.type.name === PRINTOUT_VARIABLE_NODE_NAME
        ? {node: selection.node, pos: selection.from}
        : null;
};

export const hasPrintoutVariableMark = (node: ProseMirrorNode, markName: string) => (
    node.marks.some(mark => mark.type.name === markName)
);

export const updatePrintoutVariableMark = (
    editor: Editor,
    target: PrintoutVariableTarget,
    markName: PrintoutVariableMarkName,
    attrs: Record<string, unknown> | null,
) => {
    const markType = editor.state.schema.marks[markName];
    if (!markType) return false;

    // Re-read attributes to avoid overwriting edits made after the caller rendered.
    const node = editor.state.doc.nodeAt(target.pos);
    if (!node || node.type.name !== PRINTOUT_VARIABLE_NODE_NAME) return false;

    const marks: readonly Mark[] = attrs
        ? markType.create(attrs).addToSet(node.marks)
        : node.marks.filter(mark => mark.type !== markType);

    const {tr} = editor.state;
    const wasSelected = tr.selection instanceof NodeSelection && tr.selection.from === target.pos;

    tr.setNodeMarkup(target.pos, undefined, node.attrs, marks);

    if (wasSelected) tr.setSelection(NodeSelection.create(tr.doc, target.pos));

    editor.view.dispatch(tr.scrollIntoView());

    return true;
};

export const updateSelectedPrintoutVariableMark = (
    editor: Editor,
    markName: PrintoutVariableMarkName,
    attrs: Record<string, unknown> | null,
) => {
    const target = getSelectedPrintoutVariable(editor);

    return target ? updatePrintoutVariableMark(editor, target, markName, attrs) : false;
};

export const togglePrintoutVariableMark = (
    editor: Editor,
    target: PrintoutVariableTarget,
    markName: PrintoutVariableFormatMark,
) => updatePrintoutVariableMark(
    editor,
    target,
    markName,
    hasPrintoutVariableMark(target.node, markName) ? null : {},
);

export const toggleSelectedPrintoutVariableMark = (
    editor: Editor,
    markName: PrintoutVariableFormatMark,
) => {
    const target = getSelectedPrintoutVariable(editor);

    return target ? togglePrintoutVariableMark(editor, target, markName) : false;
};

const getPrintoutVariableMarkColor = (
    node: ProseMirrorNode,
    markName: "highlight" | "textStyle",
) => {
    const value = node.marks.find(mark => mark.type.name === markName)?.attrs?.color;

    return typeof value === "string" && value.trim() ? value : null;
};

const canToggleBulletList = (editor: Editor) => (
    editor.can().chain().toggleBulletList().run()
    || isHeadingBlockFormat(getCurrentBlockFormat(editor))
    || isInList(editor)
);

const canToggleOrderedList = (editor: Editor) => (
    editor.can().chain().toggleOrderedList().run()
    || isHeadingBlockFormat(getCurrentBlockFormat(editor))
    || isInList(editor)
);

const canSetParagraphVariant = (editor: Editor, variant: PrintoutParagraphVariant) => (
    editor.can().chain().setNode('paragraph', {variant}).run()
    || isInList(editor)
);

const canSetHeading = (editor: Editor, level: HeadingLevel) => (
    editor.can().chain().setHeading({level}).run()
    || isInList(editor)
);

const canToggleBlockquote = (editor: Editor) => {
    const canCommands = editor.can() as typeof editor.commands & {
        chain?: () => {
            toggleBlockquote?: () => {run: () => boolean};
        };
        toggleBlockquote?: () => boolean;
    };

    if (typeof canCommands.toggleBlockquote === "function") {
        return canCommands.toggleBlockquote();
    }

    const chainCommands = canCommands.chain?.();
    if (typeof chainCommands?.toggleBlockquote === "function") {
        return chainCommands.toggleBlockquote().run();
    }

    return false;
};

const isBlockNodeSelected = (editor: Editor) => {
    const {selection} = editor.state;

    return selection instanceof NodeSelection && selection.node.type.name !== PRINTOUT_VARIABLE_NODE_NAME;
};

export const getToolbarState = (editor: Editor): ToolbarState => {
    // Subscriptions can still read the previous instance while useEditor replaces it.
    if (editor.isDestroyed) return DEFAULT_TOOLBAR_STATE;

    const chip = getSelectedPrintoutVariable(editor);
    const isMarkOn = (markName: string) => (
        chip ? hasPrintoutVariableMark(chip.node, markName) : editor.isActive(markName)
    );
    const canExcludedMark = (markName: typeof PRINTOUT_VARIABLE_EXCLUDED_MARKS[number], can: boolean) => (
        chip && PRINTOUT_VARIABLE_EXCLUDED_MARKS.includes(markName) ? false : can
    );

    return {
        canAlignCenter: editor.can().chain().setTextAlign('center').run(),
        canAlignJustify: editor.can().chain().setTextAlign('justify').run(),
        canAlignLeft: editor.can().chain().setTextAlign('left').run(),
        canAlignRight: editor.can().chain().setTextAlign('right').run(),
        canBlockquote: canToggleBlockquote(editor),
        canBold: !!chip || editor.can().chain().toggleBold().run(),
        canBlockFormat: Object.fromEntries(BLOCK_FORMATS.map(format => {
            const level = HEADING_LEVEL_BY_BLOCK_FORMAT[format];
            return [format, isParagraphBlockFormat(format)
                ? canSetParagraphVariant(editor, format)
                : !!level && canSetHeading(editor, level)];
        })) as Record<BlockFormat, boolean>,
        canBulletList: canToggleBulletList(editor),
        canCode: canExcludedMark('code', editor.can().chain().toggleCode().run()),
        canItalic: !!chip || editor.can().chain().toggleItalic().run(),
        canOrderedList: canToggleOrderedList(editor),
        canRedo: editor.can().redo(),
        canStrike: !!chip || editor.can().chain().toggleStrike().run(),
        canSubscript: canExcludedMark('subscript', editor.can().chain().toggleSubscript().run()),
        canSuperscript: canExcludedMark('superscript', editor.can().chain().toggleSuperscript().run()),
        canUndo: editor.can().undo(),
        canUnderline: !!chip || editor.can().chain().toggleUnderline().run(),
        invisibleCharactersVisible: editor.storage.invisibleCharacters?.visibility?.() ?? false,
        isBold: isMarkOn('bold'),
        isBulletList: editor.isActive('bulletList'),
        isCode: isMarkOn('code'),
        isItalic: isMarkOn('italic'),
        isOrderedList: editor.isActive('orderedList'),
        isStrike: isMarkOn('strike'),
        isSubscript: isMarkOn('subscript'),
        isSuperscript: isMarkOn('superscript'),
        isUnderline: isMarkOn('underline'),
        isBlockquote: editor.isActive('blockquote'),
        blockFormat: getCurrentBlockFormat(editor),
        highlightColor: chip
            ? getPrintoutVariableMarkColor(chip.node, "highlight")
            : editor.getAttributes('highlight').color ?? null,
        textAlign: getCurrentTextAlign(editor),
        textColor: chip
            ? getPrintoutVariableMarkColor(chip.node, "textStyle")
            : editor.getAttributes('textStyle').color ?? null,
        isBlockSelected: isBlockNodeSelected(editor),
    };
};
